'use client';

import { CredentialType } from '#/components/ethereum/credential-type';
import { Timestamp } from '#/components/timestamp';
import { Button } from '#/components/ui/button';
import { Checkbox } from '#/components/ui/checkbox';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '#/components/ui/form';
import { Label } from '#/components/ui/label';
import { Skeleton } from '#/components/ui/skeleton';
import { Switch } from '#/components/ui/switch';
import { BeaconStatus } from '#/constants/pectra/beacon-status';
import { ValidatorStatusEnum } from '#/constants/validator-status';
import {
  EthereumStakingOrderInfo,
  EthereumStakingValidatorInfo,
  orderList_queryFn,
  orderList_queryKey,
} from '#/data/react-query/queries/order-list';
import { cn } from '#/lib/utils';
import { OrderDisplayName } from '#/modules/display/order-display-name';
import { ValidatorIndex } from '#/modules/display/validator-index';
import { ValidatorStatus } from '#/modules/display/validator-status';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useActor } from '@xstate/react';
import { Translation } from 'intl-lib';
import { Loader2 } from 'lucide-react';
import { useCallback, useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { formatEther, parseEther } from 'viem';
import { z } from 'zod';
import { MachineContext } from '../contexts/machine';

export function SourceValidators() {
  const interpreter = useContext(MachineContext);
  const [state, dispatch] = useActor(interpreter);
  const originalRow = state.context.originalRow;
  const target = state.context.target!;
  const initalSources = state.context.sources;
  const isConsolidatableOnly = state.context.isConsolidatableOnly;

  const { data, isLoading } = useQuery({
    queryKey: orderList_queryKey({ limit: 100, offset: 0 }),
    queryFn: orderList_queryFn,
  });

  const isValidatorConsolidatable = useCallback(
    (validator: EthereumStakingValidatorInfo) => {
      return (
        validator.validatorId !== target.validatorId &&
        validator.status === ValidatorStatusEnum.ACTIVE && // isActive
        validator.exit != 1 && // not isExiting
        validator.beaconStatus === BeaconStatus.ACTIVE_ONGOING // isActiveOngoing
      );
    },
    [target]
  );

  const isValidatorVisible = useCallback(
    (validator: EthereumStakingValidatorInfo) => {
      return !isConsolidatableOnly || isValidatorConsolidatable(validator);
    },
    [isConsolidatableOnly, isValidatorConsolidatable]
  );

  const isOrderVisible = useCallback(
    (order: EthereumStakingOrderInfo) =>
      order.validators != null &&
      (!isConsolidatableOnly ||
        order.validators?.some(validator => isValidatorVisible(validator))),
    [isConsolidatableOnly, isValidatorVisible]
  );

  const formSchema = useMemo(
    () =>
      z.object({
        sources: z
          .array(
            z.custom<EthereumStakingValidatorInfo>()
            // z.any().transform(val => val as EthereumStakingValidatorInfo)
          )
          .min(1, 'At least one validator is required for consolidation.')
          .refine(value => {
            const totalBalance = value.reduce(
              (sum, item) => sum + parseEther(item.effectiveBalance),
              parseEther(target.effectiveBalance)
            );
            return totalBalance <= parseEther('2048');
          }, 'Max stake per validator after consolidation: 2048 ETH.'),
      }),
    [, target]
  );
  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sources: initalSources ?? [],
    },
  });
  // 2. Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    console.log('onSubmit', values);
    const { sources } = values;
    dispatch({
      type: 'NEXT',
      data: {
        sources,
      },
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>
          <Translation path="my_staking.consolidate_dialog.SelectSourceValidators.Title" />
        </DialogTitle>
        <DialogDescription>
          <Translation path="my_staking.consolidate_dialog.SelectSourceValidators.Description" />
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-4">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <Label>
                  <Translation path="my_staking.consolidate_dialog.SourceValidators" />
                </Label>
                <Label className="flex items-center gap-2">
                  <Switch
                    checked={isConsolidatableOnly}
                    onCheckedChange={checked =>
                      dispatch({
                        type: 'SET_STATE',
                        data: { isConsolidatableOnly: checked },
                      })
                    }
                  />
                  <p className="text-xs font-normal truncate">
                    <Translation path="my_staking.consolidate_dialog.consolidatable_validators_only" />
                  </p>
                </Label>
              </div>
              <div className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="sources"
                  render={({ field }) => (
                    <FormItem>
                      <div className="relative overflow-hidden">
                        <dl className="max-h-80 overflow-auto border border-gray-200 rounded-xl flex flex-col gap-4 p-3">
                          {isLoading ? (
                            <div className="flex flex-col gap-3">
                              <div className="flex justify-between gap-2">
                                <Skeleton className="w-24 h-4" />
                                <Skeleton className="w-32 h-4" />
                              </div>
                              <div className="flex flex-col gap-3">
                                <div className="grid grid-cols-4 gap-2">
                                  <Skeleton className="w-16 h-4" />
                                  <Skeleton className="w-16 h-4" />
                                  <Skeleton className="w-16 h-4" />
                                  <Skeleton className="w-16 h-4" />
                                </div>
                                <div className="grid grid-cols-4 gap-2">
                                  <Skeleton className="w-16 h-4" />
                                  <Skeleton className="w-16 h-4" />
                                  <Skeleton className="w-16 h-4" />
                                  <Skeleton className="w-16 h-4" />
                                </div>
                              </div>
                            </div>
                          ) : (
                            data?.data?.data?.data.map(order => {
                              return (
                                isOrderVisible(order) && (
                                  <div
                                    key={order.paramsSig}
                                    className="flex flex-col gap-3"
                                  >
                                    <dt>
                                      <div className="flex items-center justify-between">
                                        <Label className="text-muted-foreground">
                                          <OrderDisplayName item={order} />
                                        </Label>
                                        <div className="text-sm text-muted-foreground">
                                          <Timestamp date={order.createTime} />
                                        </div>
                                      </div>
                                    </dt>
                                    <div className="flex flex-col gap-1">
                                      <dd>
                                        <div className="grid grid-cols-4 items-center gap-2 text-xs text-muted-foreground">
                                          <div className="flex items-center gap-2 pl-8">
                                            <Translation path="my_staking.order.validator_column.validator" />
                                          </div>
                                          <div className="truncate">
                                            <Translation path="my_staking.order.validator_column.credential_type" />
                                          </div>
                                          <div>
                                            <Translation path="my_staking.order.validator_column.balance" />
                                          </div>
                                          <div>
                                            <Translation path="my_staking.order.validator_column.status" />
                                          </div>
                                        </div>
                                      </dd>
                                      <dd className="flex flex-col">
                                        {order.validators?.map(validator => {
                                          const isConsolidatable =
                                            isValidatorConsolidatable(
                                              validator
                                            );
                                          return !isConsolidatableOnly ||
                                            isConsolidatable ? (
                                            <FormItem
                                              key={validator.validatorIndex}
                                            >
                                              <Label
                                                className={cn(
                                                  'h-9 px-2 grid grid-cols-4 gap-2 text-sm items-center rounded-md hover:bg-muted/50',
                                                  {
                                                    'cursor-not-allowed':
                                                      !isConsolidatable,
                                                  }
                                                )}
                                              >
                                                <div className="flex items-center gap-2">
                                                  {isConsolidatable ? (
                                                    <FormControl>
                                                      <Checkbox
                                                        checked={field.value.some(
                                                          item =>
                                                            item.validatorIndex ==
                                                            validator.validatorIndex
                                                        )}
                                                        onCheckedChange={checked => {
                                                          return checked
                                                            ? field.onChange(
                                                                field.value.concat(
                                                                  validator
                                                                )
                                                              )
                                                            : field.onChange(
                                                                field.value.filter(
                                                                  item =>
                                                                    item.validatorIndex !==
                                                                    validator.validatorIndex
                                                                )
                                                              );
                                                        }}
                                                      />
                                                    </FormControl>
                                                  ) : (
                                                    <div className="size-4" />
                                                  )}
                                                  <ValidatorIndex
                                                    validatorIndex={
                                                      validator.validatorIndex
                                                    }
                                                    publicKey={validator.pubKey}
                                                  />
                                                </div>
                                                <div>
                                                  <CredentialType
                                                    credentialType={
                                                      validator.credentialType
                                                    }
                                                  />
                                                </div>
                                                <div>
                                                  {validator.effectiveBalance}{' '}
                                                  ETH
                                                </div>
                                                <div>
                                                  <ValidatorStatus
                                                    item={validator}
                                                  />
                                                </div>
                                              </Label>
                                            </FormItem>
                                          ) : null;
                                        })}
                                      </dd>
                                    </div>
                                  </div>
                                )
                              );
                            })
                          )}
                        </dl>
                      </div>
                      <FormDescription>
                        <Translation path="my_staking.consolidate_dialog.SelectSourceValidators.SelectedValidators" />{' '}
                        <FormField
                          control={form.control}
                          name="sources"
                          render={({ field }) => {
                            const totalBalance = field.value.reduce(
                              (sum, item) =>
                                sum + parseEther(item.effectiveBalance),
                              parseEther('0')
                            );
                            return (
                              <>
                                {field.value.length} (
                                {formatEther(totalBalance)} ETH)
                              </>
                            );
                          }}
                        />
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </form>
        </Form>
        <div className="flex flex-col gap-2">
          <Label>
            <Translation path="my_staking.consolidate_dialog.DestinationValidator" />
          </Label>
          <div className="border rounded-xl p-3 flex flex-col gap-1">
            <div className="grid grid-cols-4 items-center gap-2 text-xs text-muted-foreground">
              <div>
                <Translation path="my_staking.order.validator_column.validator" />
              </div>
              <div className="truncate">
                <Translation path="my_staking.order.validator_column.credential_type" />
              </div>
              <div>
                <Translation path="my_staking.order.validator_column.balance" />
              </div>
              <div>
                <Translation path="my_staking.order.validator_column.status" />
              </div>
            </div>
            <div className="h-9 grid grid-cols-4 items-center gap-2 text-sm font-medium">
              <div>
                <ValidatorIndex
                  validatorIndex={target.validatorIndex}
                  publicKey={target.pubKey}
                />
              </div>
              <div>
                <CredentialType credentialType={target.credentialType} />
              </div>
              <div>{target.effectiveBalance} ETH</div>
              <div>
                <ValidatorStatus item={target} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            dispatch('PREV');
          }}
        >
          <Translation path="my_staking.Validators.Dialog.PreviousButtonLabel" />
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={form.formState.isSubmitting}
          onClick={form.handleSubmit(onSubmit)}
        >
          {form.formState.isSubmitting && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          <Translation path="my_staking.Validators.Dialog.NextButtonLabel" />
        </Button>
      </DialogFooter>
    </div>
  );
}
